import { getCpiForCountry, normalizeRef } from "@/lib/cpi";
import { Game } from "@/types";
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, query, getDocs, getFirestore, where, orderBy } from 'firebase/firestore';

export interface InstallEventPayload {
  country: string;
  campaignId: string;
  gameSlug: string;
  accountId: string;
  installs: number;
}

export function calculateEarningsFromEvent(
  event: InstallEventPayload,
  game?: Game
) {
  const cpi = getCpiForCountry(event.country, game?.cpiByCountry);
  return Number((cpi * event.installs).toFixed(2));
}

export function buildGa4EventName(campaignSlug: string, gameSlug: string) {
  return `install-${campaignSlug}-${gameSlug}`;
}

/**
 * Sync GA4 install events for a user's social account.
 * Calls the Cloud Function `fetchUserEvents` which queries GA4,
 * filters by the ref tag, calculates earnings, and stores in Firestore.
 * 
 * @param refTag The user's social account handle (ref tag)
 * @param startDate Optional start date (default: '30daysAgo')
 * @param endDate Optional end date (default: 'today')
 * @returns Promise with { success, count, totalEarnings }
 */
export async function syncUserInstallEvents(
  refTag: string,
  startDate = '30daysAgo',
  endDate = 'today'
): Promise<{ success: boolean; count: number; totalEarnings: number }> {
  const functions = getFunctions();
  const fetchUserEvents = httpsCallable<
    { refTag: string; startDate?: string; endDate?: string },
    { success: boolean; count: number; totalEarnings: number }
  >(functions, 'fetchUserEvents');

  try {
    const result = await fetchUserEvents({ refTag, startDate, endDate });
    return result.data;
  } catch (error) {
    console.error('Failed to sync install events:', error);
    throw error;
  }
}

/**
 * Fetch install events from Firestore for the current user.
 * Used for displaying earnings timeline and stats.
 * 
 * @param userId Firebase Auth UID
 * @param gameFilter Optional game name to filter by
 * @returns Array of install events
 */
export async function getInstallEvents(userId: string, gameFilter?: string) {
  const db = getFirestore();
  const eventsRef = collection(db, `users/${userId}/installEvents`);
  
  // Normalize the incoming game filter so slugs like `shawarma-run` match stored
  // parsed game names which are alphanumeric (e.g., `shawarmarun`).
  let q = query(eventsRef, orderBy('timestamp', 'desc'));
  if (gameFilter) {
    const norm = normalizeRef(gameFilter);
    q = query(eventsRef, where('game', '==', norm), orderBy('timestamp', 'desc'));
  }
  
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Array<{
    id: string;
    game: string;
    platform: string;
    ref: string;
    country: string;
    date: string;
    timestamp: any; // Firestore Timestamp object or string or number
    cpi: number;
    eventCount: number;
  }>;
}

